import { ActionButton } from './ActionButton.js'
import { notifyError } from './Toasts.js'

/**
 * Der Editor vor dem Versand.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum es ihn geben muss
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Bis zum Hotfix löste ein Klick auf «An Kunden senden» unmittelbar den
 * Versand aus. Betreff und Text entstanden mitten in der Serverroute, als
 * zusammengesetzte Zeichenkette – niemand im Betrieb hatte sie je gesehen,
 * und ändern konnte sie niemand. Was beim Kunden ankam, war ein Zweizeiler.
 *
 * Jetzt steht hier die fertige Nachricht: Empfänger, Betreff, Text, Anhang.
 * Betreff und Text sind Felder, keine Anzeige – der Betrieb kennt seinen
 * Kunden, wir kennen ihn nicht. Gesendet wird erst mit dem Knopf darin.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Zwei Dinge, die der Editor NICHT tut
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Er lässt die Empfängeradresse nicht ändern. Sie kommt aus dem
 * Kundendatensatz, und eine frei tippbare Adresse im Versanddialog wäre der
 * kürzeste Weg, eine Rechnung an die falsche Firma zu schicken – dafür gibt
 * es den Kundendatensatz, wo die Korrektur auch bleibt.
 *
 * Er schliesst sich nicht, wenn der Versand scheitert. Wer eine Nachricht
 * umformuliert hat und sie wegen eines Mailserverfehlers neu tippen müsste,
 * verlöre zweimal. Das Schliessen übernimmt der Aufrufer, und zwar erst nach
 * einem erfolgreichen Versand.
 */
export interface Mailentwurf {
  weg: 'benutzer' | 'firma' | 'kunde'
  empfaenger: string
  betreff: string
  text: string
}

export function MailEditor({
  entwurf,
  titel,
  anhang,
  aufAendern,
  aufSenden,
  aufAbbrechen
}: {
  entwurf: Mailentwurf
  titel: string
  /** Der Dateiname des Anhangs – immer das aktuelle PDF des Belegs. */
  anhang: string
  aufAendern: (e: Mailentwurf) => void
  aufSenden: () => Promise<void>
  aufAbbrechen: () => void
}): JSX.Element {
  const leer = entwurf.betreff.trim() === '' || entwurf.text.trim() === ''
  return (
    <div className="o-dialog-grund" id="mail-editor">
      <div className="o-dialog o-dialog-breit">
        <h3>{titel}</h3>

        <div className="o-field">
          <label htmlFor="mail-empfaenger">Empfänger</label>
          <input id="mail-empfaenger" value={entwurf.empfaenger} readOnly autoComplete="off" />
          <span className="hint">
            {entwurf.weg === 'kunde'
              ? 'Die Adresse kommt aus dem Kundendatensatz. Korrigieren lässt sie sich dort.'
              : 'Von dieser Adresse aus kannst du das PDF selbst weiterleiten.'}
          </span>
        </div>

        <div className="o-field">
          <label htmlFor="mail-betreff">Betreff</label>
          <input
            id="mail-betreff"
            value={entwurf.betreff}
            maxLength={200}
            autoComplete="off"
            onChange={(e) => aufAendern({ ...entwurf, betreff: e.target.value })}
          />
        </div>

        <div className="o-field">
          <label htmlFor="mail-text">Nachricht</label>
          <textarea
            id="mail-text"
            rows={12}
            value={entwurf.text}
            maxLength={8000}
            onChange={(e) => aufAendern({ ...entwurf, text: e.target.value })}
          />
          <span className="hint">
            Was hier steht, geht so hinaus. Die Vorlage lässt sich frei ändern.
          </span>
        </div>

        <p className="muted small" id="mail-anhang">
          Anhang: {anhang} — das aktuelle PDF dieses Belegs.
        </p>

        <div className="rowactions">
          <ActionButton
            id="btn-mail-senden"
            actionClass="B"
            disabled={leer}
            labels={{ idle: 'E-Mail senden', busy: 'Sendet …', success: 'Gesendet' }}
            onAction={aufSenden}
            onError={(err) => notifyError(err)}
          />
          <ActionButton
            variant="quiet"
            labels={{ idle: 'Abbrechen', busy: '…' }}
            onAction={async () => aufAbbrechen()}
          />
        </div>
      </div>
    </div>
  )
}
